import { atom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { ROUTES } from '../constants/RouteNameMapping';
import { COMPANY_VIEW_TYPE, ViewTypeByData } from '../data-models/view-config.data-model';
import { ViewModel } from '../models/view.model';
import { selectedViewPFLoadableJ } from '../pages/PortfolioOverview/state/ViewStateJ';
import { defaultCurrency } from '../services/state/AppConfigStateJ';
import { selectedCurrencyPrefAtom } from '../services/state/CurrencyStateJ';
import { useSelectedCurrency } from './useSelectedCurrency';

/** Views whose metrics are read in the selected currency; the toggle shows only on these. */
export function readsSelectedCurrency(view: ViewModel | null | undefined): boolean {
  return (
    view?.viewType === ViewTypeByData.company ||
    view?.viewType === ViewTypeByData.fund ||
    view?.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER
  );
}

const noViewAtom = atom(null);

/**
 * The toggle rewrites the app-wide money formatters, so the saved currency applies only on portfolio
 * views that read in it; every other page, and portfolio views that read USD, format in USD.
 * Mounted with the app shell: a hook inside the portfolio nav unmounts before it can see the exit.
 */
export function usePortfolioCurrencyScope() {
  const { pathname } = useLocation();
  const inPortfolio = pathname.startsWith(`/${ROUTES.PORTFOLIO}`);
  // Outside the portfolio the views are not read, so leaving does not fetch them.
  const viewLoadable = useAtomValue(inPortfolio ? selectedViewPFLoadableJ : noViewAtom);
  const view = viewLoadable?.state === 'hasData' ? viewLoadable.data : null;
  const { currency: savedCurrency } = useAtomValue(selectedCurrencyPrefAtom);
  const { selectedCurrency, setSelectedCurrency } = useSelectedCurrency();

  // A view still loading keeps the saved currency, so its first fetch is already in it.
  const target =
    inPortfolio && (!view || readsSelectedCurrency(view))
      ? (savedCurrency ?? defaultCurrency)
      : defaultCurrency;

  useEffect(() => {
    if (target !== selectedCurrency) {
      setSelectedCurrency(target, false);
    }
  }, [selectedCurrency, setSelectedCurrency, target]);
}
